import { useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { SimplePagination } from './SimplePagination';
import { TimeScopePicker } from './time-scope-picker/TimeScopePicker';
import { TimeScope, TimeScopeMode } from './time-scope-picker/types';
import { useTimeScopeSiblings } from './time-scope-picker/useTimeScopeActions';
import { getScopeLabel } from './time-scope-picker/utils';
import { useJSONSearchParam } from '../hooks/useJSONSearchParam';
import { useOnClickOutside } from '../hooks/useOnClickOutside';

type Props = {
  minDate?: number;
  maxDate?: number;
  defaultTimeScope: TimeScope;
};

export const SCOPE_DATA_SEARCH_PARAM_KEY = 'scopeData';

export type ScopeData = {
  scope: TimeScope;
  mode: TimeScopeMode;
};

export const TimeScopePagination = ({ minDate, maxDate, defaultTimeScope }: Props) => {
  const [data, setData] = useJSONSearchParam<ScopeData>(SCOPE_DATA_SEARCH_PARAM_KEY, {
    scope: defaultTimeScope,
    mode: 'fortnight',
  });
  const [isVisible, setIsVisible] = useState(false);

  const { nextPeriod, previousPeriod } = useTimeScopeSiblings(data.scope, data.mode, minDate, maxDate);

  const ref = useOnClickOutside(
    useCallback(() => {
      setIsVisible(false);
      setData({ ...data }); // Resets the date picker to the current date
    }, [data, setData]),
  );

  const handleNext = useCallback(() => {
    if (nextPeriod) {
      setData({ scope: nextPeriod, mode: data.mode });
    }
  }, [data.mode, nextPeriod, setData]);

  const handlePrevious = useCallback(() => {
    if (previousPeriod) {
      setData({ scope: previousPeriod, mode: data.mode });
    }
  }, [data.mode, previousPeriod, setData]);

  const handleChange = useCallback(
    (newScope?: TimeScope, mode?: TimeScopeMode) => {
      if (newScope && mode) {
        setData({ scope: newScope, mode: mode });
      } else {
        setData({ scope: defaultTimeScope, mode: 'fortnight' });
      }
      setIsVisible(false);
    },
    [defaultTimeScope, setData],
  );

  return (
    <div className="relative">
      <SimplePagination
        onNext={handleNext}
        onPrevious={handlePrevious}
        isNextDisabled={!nextPeriod}
        isPreviousDisabled={!previousPeriod}
        onHeaderClick={() => setIsVisible(true)}
      >
        <span className="capitalize">{getScopeLabel(data.scope, data.mode)}</span>
      </SimplePagination>

      <div
        ref={ref}
        className={twMerge(
          'absolute z-10 inline-block top-12  px-3 py-2 text-sm text-gray-500 transition-opacity duration-300 bg-white border border-gray-200 rounded-lg shadow-sm dark:text-gray-400 dark:border-gray-600 dark:bg-gray-800',
          !isVisible && 'invisible opacity-0',
        )}
      >
        <TimeScopePicker
          mode={data.mode}
          onScopeChange={handleChange}
          scope={data.scope}
          minDate={minDate}
          maxDate={maxDate}
          defaultTimeScope={defaultTimeScope}
        />
      </div>
    </div>
  );
};
